import { cn } from '@dinewise/utils';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import { motion } from 'motion/react';

type Props = {
  theme?: 'dots' | 'arrows' | 'spinner' | 'spinner-gradient' | 'bar';
  classNames?: {
    dots?: string;
    arrows?: string;
    spinner?: string;
    spinnerGradient?: string;
    bar?: {
      container?: string;
      highlight?: string;
    };
  };
};

export const ActivityIndicator = ({ classNames, theme = 'spinner' }: Props) => {
  switch (theme) {
    case 'dots': {
      return (
        <div
          className={cn(
            'flex items-center justify-center space-x-1 text-gray-400',
            classNames?.dots,
          )}
        >
          <span>&nbsp;</span>
          <div className="size-1 animate-bounce-1 rounded-full bg-current" />
          <div className="size-1 animate-bounce-2 rounded-full bg-current" />
          <div className="size-1 animate-bounce-3 rounded-full bg-current" />
          <span>&nbsp;</span>
        </div>
      );
    }
    case 'arrows': {
      return (
        <ArrowPathIcon
          className={cn('animate-spin [animation-duration:1.5s]', classNames?.arrows)}
        />
      );
    }
    case 'spinner-gradient': {
      return (
        <div
          className={cn(
            'spinner-gradient animate-[spin_1.5s_linear_infinite] text-ande-black',
            classNames?.spinnerGradient,
          )}
        />
      );
    }
    case 'bar': {
      return (
        <div
          className={cn(
            'relative animate-pulse overflow-hidden bg-gray-200',
            classNames?.bar?.container,
          )}
        >
          <motion.div
            className={cn('absolute h-full w-1/3 shadow-sm', classNames?.bar?.highlight)}
            initial={{ x: '-100%', opacity: 1 }}
            animate={{ x: '400%', opacity: [1, 0.7, 1] }}
            transition={{
              x: { duration: 1.5, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' },
              opacity: { duration: 1.5, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' },
            }}
          />
        </div>
      );
    }
    default: {
      return (
        <svg
          className={cn('animate-spin', classNames?.spinner)}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      );
    }
  }
};
